import { useCallback, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import NotFoundPage from "components/error/NotFoundPage";
import { User } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Counter from "ds/components/Counter";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import PageInfoHeader from "ds/components/PageInfoHeader";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import useErrorHandle from "hooks/useErrorHandle";
import useGetSpaceliftVcsRepo from "shared/SpaceliftVcsRepo/useGetSpaceliftVcsRepo";
import useGetSpaceliftVcsRepoCommit from "shared/SpaceliftVcsRepo/useGetSpaceliftVcsRepoCommit";
import { getRepoRevisionsPageUrl, getRepoUrl, REPOS_PAGE_URL } from "utils/spaceliftRepo";

import { SPACELIFT_VCS_REPO_SORT_STORAGE_KEY } from "../helpers";
import { SPACELIFT_VCS_REPO_COMMIT_SEARCH_FILTERS_STORAGE_KEY } from "./constants";
import { showCommitDetailsDrawer } from "./DetailsDrawer";
import SpaceliftVcsRepoCommitFileSection from "./FileSection";
import SpaceliftVcsRepoCommitFileTree from "./FileTree";
import filterCommitFiles from "./filterCommitFiles";
import { buildTreeNodes } from "./helpers";
import { SHORT_SHA_LENGTH } from "../constants";

const SpaceliftVcsRepoCommit = () => {
  const { repoId, sha } = useParams<{ repoId: string; sha: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = getSearchQuery(searchParams);
  const { repo, loading: repoLoading, error: repoError } = useGetSpaceliftVcsRepo({ repoId });
  const {
    commit,
    loading: commitLoading,
    error: commitError,
  } = useGetSpaceliftVcsRepoCommit({
    repoId: repoId!,
    sha: sha!,
  });

  const [selectedFilePath, setSelectedFilePath] = useState<string | undefined>();
  const [collapseState, setCollapseState] = useState<Record<string, boolean>>({});
  const fileRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const shortSha = sha?.slice(0, SHORT_SHA_LENGTH) ?? "";
  const repoUrl = getRepoUrl(repoId!);

  useBreadcrumbs(
    [
      {
        title: "Repos",
        link: getBreadcrumbsBackUrl(REPOS_PAGE_URL, SPACELIFT_VCS_REPO_SORT_STORAGE_KEY),
      },
      {
        title: repo?.name || "",
        link: repoUrl,
      },
      {
        title: "Commits",
        link: getRepoRevisionsPageUrl(repoId!),
      },
      {
        title: `Commit ${shortSha}`,
      },
    ],
    [repo?.name, repoUrl, shortSha]
  );

  const handleFileSelect = useCallback((filePath: string) => {
    setSelectedFilePath(filePath);
    setCollapseState((prev) => ({ ...prev, [filePath]: false }));
    const element = fileRefs.current.get(filePath);
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleToggleFile = useCallback((filePath: string, isCollapsed: boolean) => {
    setCollapseState((prev) => ({ ...prev, [filePath]: !isCollapsed }));
  }, []);

  const setFileRef = useCallback((filePath: string, element: HTMLDivElement | null) => {
    if (element) {
      fileRefs.current.set(filePath, element);
    } else {
      fileRefs.current.delete(filePath);
    }
  }, []);

  const handleOpenDetailsDrawer = useCallback(() => {
    if (commit) {
      showCommitDetailsDrawer({ commit });
    }
  }, [commit]);

  const files = useMemo(() => commit?.files ?? [], [commit]);

  const filteredFiles = useMemo(() => filterCommitFiles(files, searchQuery), [files, searchQuery]);

  const filteredTreeNodes = useMemo(() => buildTreeNodes(filteredFiles), [filteredFiles]);

  const ErrorContent = useErrorHandle(repoError || commitError);

  if (ErrorContent) {
    return ErrorContent;
  }

  if ((repoLoading && !repo) || (commitLoading && !commit)) {
    return <PageLoading />;
  }

  if (!commit) {
    return <NotFoundPage />;
  }

  const commitAuthor = commit.authorEmail || commit.authorName;

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>Commit {shortSha}</ViewHeaderTitle>
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
              Details
            </Button>
            <DropdownMenuEllipsis tooltip="Commit actions" dotsSize="medium">
              <CopyFieldDropdownMenuItem value={commit.sha} title="Copy SHA" />
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <TimestampMetaInfo timestamp={commit.createdAt} />
            {commitAuthor && <MetaInfoListItem icon={User}>{commitAuthor}</MetaInfoListItem>}
          </MetaInfoList>
        </ViewHeaderScrollCollapse>
      </ViewHeader>

      <PageInfoHeader
        title={
          <Box direction="row" align="center" gap="small">
            <PageInfoHeaderTitle title="Files changed" />
            <Counter count={filteredFiles.length} />
          </Box>
        }
      >
        <SearchInput
          placeholder="Search files..."
          filtersOrderSettingsKey={SPACELIFT_VCS_REPO_COMMIT_SEARCH_FILTERS_STORAGE_KEY}
        />
      </PageInfoHeader>

      <Box direction="row" fullWidth grow="1">
        <SpaceliftVcsRepoCommitFileTree
          key={searchQuery}
          treeNodes={filteredTreeNodes}
          selectedFilePath={selectedFilePath}
          onChange={handleFileSelect}
        />

        <Box direction="column" gap="medium" fullWidth fullHeight>
          {filteredFiles.length === 0 && searchQuery && (
            <Box direction="row" justify="center" padding="large">
              No matching files
            </Box>
          )}

          {filteredFiles.map((file, index) => {
            const isCollapsed =
              file.filePath in collapseState ? collapseState[file.filePath] : index !== 0;

            return (
              <SpaceliftVcsRepoCommitFileSection
                key={file.filePath}
                repoId={repoId!}
                file={file}
                isCollapsed={isCollapsed}
                onToggle={() => handleToggleFile(file.filePath, isCollapsed)}
                ref={(element) => setFileRef(file.filePath, element)}
              />
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default SpaceliftVcsRepoCommit;
